"use client";

import React from "react";
import { PhoneCall, Wrench, Clock, ShieldCheck, CircleAlert as AlertCircle, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqList: FaqItem[] = [{
  id: "item-1",
  question: "Why is my toilet constantly running or cycling water?",
  answer: "A constantly running toilet is usually caused by a faulty flapper that fails to seal properly, an incorrectly adjusted float arm, or a worn-out fill valve. This problem wastes gallons of water every day and can quickly elevate your monthly water bill if not promptly repaired."
}, {
  id: "item-2",
  question: "What should I do immediately if my toilet begins to overflow?",
  answer: "Locate the shut-off valve behind the toilet near the wall and turn it clockwise until the water stops completely. If the valve is stuck, carefully lift the tank lid and push down the flapper or lift the float arm to pause incoming flow before calling our emergency team."
}, {
  id: "item-3",
  question: "Why is water pooling around the base of my toilet bowl?",
  answer: "Water pooling around the base typically indicates a degraded wax ring seal or loose closet bolts connecting the toilet to the floor flange. It can also stem from a hairline porcelain crack. Operating a leaking base can damage your subfloor, requiring prompt professional resealing."
}, {
  id: "item-4",
  question: "When should I repair my toilet versus replacing it entirely?",
  answer: "Minor valve replacements, flapper fixes, and seal replacements are straightforward repairs. However, if your toilet has persistent porcelain cracks, requires frequent repairs, or is an outdated model using excessive water per flush, investing in a high-efficiency replacement is more cost-effective."
}, {
  id: "item-5",
  question: "How fast can an emergency plumber arrive in Jonesboro, GA?",
  answer: "Our technicians are locally stationed in Jonesboro and available 24/7. For urgent clogs, active leaks, or overflowing toilets, we prioritize immediate dispatch with fully stocked service trucks to resolve the breakdown on the first visit."
}];
export default function ToiletFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="toiletrepairfaq" data-nav="light" className="w-full bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <motion.div variants={itemVariants} className="flex justify-center mb-3">
            <Badge variant="outline" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5 border-border bg-card">
              <HelpCircle className="size-3.5 text-accent" />
              Common Questions
            </Badge>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Toilet Repair FAQs
          </motion.h2>

          <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clear answers to common toilet issues in Jonesboro homes, from running water to severe clogs and seal leaks.
          </motion.p>
        </motion.div>

        {/* Accordion FAQ Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-4">
              {faqList.map((faq, i) => <div key={faq.id} data-index={i}>
                  <AccordionItem value={faq.id} className="border border-border rounded-xl bg-card px-5 transition-colors data-[state=open]:border-accent/40 data-[state=open]:shadow-sm">
                    <AccordionTrigger className="text-left font-default text-base sm:text-lg font-semibold text-card-foreground py-5 hover:no-underline hover:text-accent focus-visible:ring-accent">
                      <span className="flex items-center gap-3">
                        <span className="flex size-7 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground shrink-0">
                          {`0${i + 1}`}
                        </span>
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-10 pb-5">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </div>

          {/* Quick Help Side Card */}
          <div className="lg:col-span-4">
            <Card className="border border-border bg-card shadow-sm rounded-2xl">
              <CardContent className="p-6">
                <div className="size-12 rounded-xl bg-secondary flex items-center justify-center text-secondary-foreground mb-5">
                  <Wrench className="size-6" />
                </div>
                <h3 className="text-xl font-default font-bold text-card-foreground mb-2">
                  Need Quick Advice?
                </h3>
                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                  Unsure whether your toilet requires a simple component fix or complete replacement? Our certified Jonesboro plumbers offer honest diagnostics.
                </p>

                <div className="space-y-3 border-t border-border pt-4 mb-6">
                  <div className="flex items-center gap-2.5 text-xs text-foreground font-medium">
                    <Clock className="size-4 text-accent shrink-0" />
                    <span>24/7 Fast Local Dispatch</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-xs text-foreground font-medium">
                    <ShieldCheck className="size-4 text-accent shrink-0" />
                    <span>Upfront Pricing, No Surprises</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-xs text-foreground font-medium">
                    <AlertCircle className="size-4 text-accent shrink-0" />
                    <span>Clean Work Guarantee</span>
                  </div>
                </div>

                <Button asChild variant="outline" className="w-full justify-center">
                  <Link to="/problems/">
                    View Common Problems
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA Service Request Banner */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground p-8 sm:p-10 lg:p-12 shadow-lg">
          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl text-center lg:text-left">
              <Badge className="bg-accent text-accent-foreground font-semibold px-3 py-1 mb-4 rounded-full">
                Emergency & Same-Day Service
              </Badge>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-default font-bold tracking-tight text-primary-foreground mb-3">
                Have a Toilet Leak or Urgent Backup in Jonesboro?
              </h3>
              <p className="text-sm sm:text-base text-primary-foreground/85 leading-relaxed">
                Do not wait for water damage to spread. Get an experienced local technician dispatched to your home immediately.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto shrink-0">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-semibold text-base h-12 px-8 rounded-xl">
                <Link to="/contact/">
                  Request Service Now
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 bg-primary/20 text-primary-foreground hover:bg-primary-foreground/10 h-12 px-6 rounded-xl font-medium">
                <a href="tel:4708479076" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call Emergency Line</span>
                </a>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}